import {GroupVerifyCriticalInformation} from 'ApplicationEntry/models/GroupVerifyCriticalInformation';
import {NewYorkVerifyCriticalInformation} from 'ApplicationEntry/models/NewYorkVerifyCriticalInformation';
import {OrdinaryVerifyCriticalInformation} from 'ApplicationEntry/models/OrdinaryVerifyCriticalInformation';

export type VerifyCriticalInformation =
  | GroupVerifyCriticalInformation
  | NewYorkVerifyCriticalInformation
  | OrdinaryVerifyCriticalInformation;

type ExhaustiveVerifyCriticalInformationOptionsByType<TReturn> = {
  Group: (group: GroupVerifyCriticalInformation) => TReturn;
  NewYork: (newYork: NewYorkVerifyCriticalInformation) => TReturn;
  Ordinary: (ordinary: OrdinaryVerifyCriticalInformation) => TReturn;
};

type PartialVerifyCriticalInformationOptionsByType<TReturn> = {
  Group?: (group: GroupVerifyCriticalInformation) => TReturn;
  NewYork?: (newYork: NewYorkVerifyCriticalInformation) => TReturn;
  Ordinary?: (ordinary: OrdinaryVerifyCriticalInformation) => TReturn;
  _: (app: VerifyCriticalInformation) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: VerifyCriticalInformationByTypeOptions<TReturn>
): options is ExhaustiveVerifyCriticalInformationOptionsByType<TReturn> =>
  !!options.Group && !!options.NewYork && !!options.Ordinary;

type VerifyCriticalInformationByTypeOptions<TReturn> =
  | PartialVerifyCriticalInformationOptionsByType<TReturn>
  | ExhaustiveVerifyCriticalInformationOptionsByType<TReturn>;

export const matchVerifyCriticalInformationByType = <TReturn>(
  options: VerifyCriticalInformationByTypeOptions<TReturn>
) => (app: VerifyCriticalInformation): TReturn => {
  switch (app.key) {
    case 'InvalidGroupVerifyCriticalInformation':
    case 'ValidGroupVerifyCriticalInformation':
    case 'UnvalidatedGroupVerifyCriticalInformation':
      return isExhaustiveByType(options) ? options.Group(app) : options.Group ? options.Group(app) : options._(app);
    case 'InvalidNewYorkVerifyCriticalInformation':
    case 'ValidNewYorkVerifyCriticalInformation':
    case 'UnvalidatedNewYorkVerifyCriticalInformation':
      return isExhaustiveByType(options)
        ? options.NewYork(app)
        : options.NewYork
        ? options.NewYork(app)
        : options._(app);
    case 'InvalidOrdinaryVerifyCriticalInformation':
    case 'ValidOrdinaryVerifyCriticalInformation':
    case 'UnvalidatedOrdinaryVerifyCriticalInformation':
      return isExhaustiveByType(options)
        ? options.Ordinary(app)
        : options.Ordinary
        ? options.Ordinary(app)
        : options._(app);
  }
};

export const isValidVerifyCriticalInformation = (verifyCriticalInformation: VerifyCriticalInformation) =>
  verifyCriticalInformation.key === 'ValidGroupVerifyCriticalInformation' ||
  verifyCriticalInformation.key === 'ValidNewYorkVerifyCriticalInformation' ||
  verifyCriticalInformation.key === 'ValidOrdinaryVerifyCriticalInformation';
